/**
 * Permission keys mapping for sidebar menu filtering
 * Maps sidebar item IDs to permission keys used in role.permissions JSONB
 */
export const SECTION_PERMISSIONS = {
  // Quick Access
  quickview: "analytics",

  // Sales
  orders: "orders",
  "order-reviews": "order_reviews",
  customers: "customers",
  payments: "payments",
  deliveries: "shipping",
  loans: "loans",

  // Expenses
  invoices: "invoices",
  suppliers: "suppliers",
  salesmans: "salespersons",

  // Production
  products: "products",
  operations: "operations",
  packaging: "operations",
  sections: "sections",

  // Analytics
  dashboards: "analytics",
  financials: "analytics",
  settings: "settings",

  // Cloud Licences
  cloudlicences: "cloud_licences",
}

/**
 * Permission presets for common roles
 * These can be used when creating new roles
 */
export const PERMISSION_PRESETS = {
  admin: {
    all: true,
  },
  b2b: {
    type: "b2b",
    store: { access: true },
  },
  retail: {
    type: "retail",
    sidebar: {
      orders: true,
      "order-reviews": true,
      customers: true,
      products: true,
      payments: true,
      settings: true,
    },
    store: { access: true, can_create_customer: true },
  },
  driver: {
    shipping: true,
    orders: { read: true },
  },
  store: {
    orders: true,
    customers: { read: true },
    products: { read: true },
  },
  production: {
    products: true,
    operations: true,
    sections: true,
  },
  accountant: {
    invoices: true,
    payments: true,
    analytics: true,
    suppliers: true,
  },
  salesperson: {
    orders: true,
    customers: true,
    products: { read: true },
  },
}

/**
 * Human-readable labels for permission keys (used in Roles management UI)
 */
export const MODULE_LABELS = {
  orders: "Orders",
  order_reviews: "Order Reviews",
  customers: "Customers",
  payments: "Payments",
  shipping: "Deliveries",
  loans: "Loans",
  invoices: "Invoices",
  suppliers: "Suppliers",
  salespersons: "Salespersons",
  products: "Products",
  operations: "Operations",
  sections: "Sections",
  analytics: "Analytics",
  settings: "Settings",
  cloud_licences: "Cloud Licences",
}

/**
 * All available permission keys
 */
export const PERMISSION_KEYS = [
  "orders",
  "order_reviews",
  "customers",
  "payments",
  "shipping",
  "loans",
  "invoices",
  "suppliers",
  "salespersons",
  "products",
  "operations",
  "sections",
  "analytics",
  "settings",
  "cloud_licences",
]

/**
 * Sidebar items available for role permissions (used in RolesTab)
 */
export const SIDEBAR_ITEMS = [
  { id: "orders", label: "Orders" },
  { id: "order-reviews", label: "Quote Reviews" },
  { id: "customers", label: "Customers" },
  { id: "products", label: "Products" },
  { id: "payments", label: "Payments" },
  { id: "deliveries", label: "Deliveries" },
  { id: "loans", label: "Loans" },
  { id: "invoices", label: "Invoices" },
  { id: "suppliers", label: "Suppliers" },
  { id: "salesmans", label: "Salespersons" },
  { id: "operations", label: "Operations" },
  { id: "packaging", label: "Packaging" },
  { id: "sections", label: "Sections" },
  { id: "dashboards", label: "Dashboards" },
  { id: "financials", label: "Financials" },
  { id: "settings", label: "Settings" },
  { id: "quickview", label: "Quick View" },
  { id: "cloudlicences", label: "Cloud Licences" },
]

/**
 * Store/CPQ permission keys for the role editor
 */
export const STORE_PERMISSIONS = [
  { key: "access", label: "Store / CPQ Access" },
  { key: "can_create_customer", label: "Can Create Customers" },
]

/**
 * Permission category tabs for the role editor
 */
export const PERMISSION_CATEGORIES = [
  { id: "sidebar", label: "Sidebar Access" },
  { id: "store", label: "Store / CPQ" },
]

// ── Role type helpers ──────────────────────────────────────────────

/**
 * Check if permissions indicate a B2B user
 */
export function isB2BUser(permissions) {
  if (!permissions) return false
  return permissions.type === "b2b"
}

/**
 * Check if permissions indicate a Retail user
 */
export function isRetailUser(permissions) {
  if (!permissions) return false
  return permissions.type === "retail"
}

/**
 * Check if permissions indicate an Admin user
 */
export function isAdminUser(permissions) {
  if (!permissions) return false
  return permissions.all === true
}

/**
 * Check if user has access to a specific sidebar item
 * Admin → all, B2B → none, Retail → check sidebar map
 */
export function hasSidebarPermission(permissions, itemId) {
  if (!permissions) return false
  if (permissions.all === true) return true
  if (permissions.type === "b2b") return false

  // Retail/custom: check sidebar map
  if (permissions.sidebar) {
    return permissions.sidebar[itemId] === true
  }

  // Legacy fallback: check old-style permission keys
  const permKey = SECTION_PERMISSIONS[itemId]
  if (!permKey) return true
  return hasPermission(permissions, permKey)
}

/**
 * Check if user has store/CPQ access
 */
export function hasStorePermission(permissions, key = "access") {
  if (!permissions) return false
  if (permissions.all === true) return true
  return permissions.store?.[key] === true
}

/**
 * Check if a user has permission for a specific action
 * @param {Object} permissions - User's permissions object
 * @param {string} key - Permission key to check
 * @param {string} action - Action type (read, write, delete) - optional
 * @returns {boolean}
 */
export function hasPermission(permissions, key, action = null) {
  if (!permissions) return false

  // Admin has all permissions
  if (permissions.all === true) return true

  const perm = permissions[key]

  // No permission for this key
  if (!perm) return false

  // Full permission (boolean true)
  if (perm === true) return true

  // Object with specific actions
  if (typeof perm === "object") {
    // If no specific action required, check if any action is allowed
    if (!action) {
      return perm.read === true || perm.write === true || perm.delete === true
    }
    // Check specific action
    return perm[action] === true
  }

  return false
}

/**
 * Check if user has any permission from a list of keys
 * @param {Object} permissions - User's permissions object
 * @param {string[]} keys - Permission keys to check
 * @returns {boolean}
 */
export function hasAnyPermission(permissions, keys) {
  if (!permissions) return false
  if (permissions.all === true) return true

  return keys.some(key => hasPermission(permissions, key))
}
